import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface AdvancedFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameName: string;
  advancedFields: string[];
  activeFilters: Record<string, string>;
  onFilterChange: (field: string, value: string) => void;
  onClearFilters: () => void;
  pretty: (key: string) => string;
}

export const AdvancedFiltersModal: React.FC<AdvancedFiltersModalProps> = ({
  isOpen,
  onClose,
  gameName,
  advancedFields,
  activeFilters,
  onFilterChange,
  onClearFilters,
  pretty
}) => {
  // Fechar com a tecla ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Contagem de filtros avançados preenchidos
  const activeCount = advancedFields.filter(f => Boolean(activeFilters[f]?.trim() || activeFilters[`${f}__all`]?.trim())).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          id="advanced-filters-modal-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
        >
          {/* Backdrop com escurecimento e leve blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
          />

          {/* Janela do Modal */}
          <motion.div
            id="advanced-filters-modal-card"
            initial={{ scale: 0.96, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl shadow-black/80 flex flex-col max-h-[90vh] overflow-hidden z-10"
          >
            {/* Cabeçalho */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/90">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center text-purple-400">
                  <i className="fas fa-sliders text-sm"></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <span>Filtros Avançados</span>
                    {activeCount > 0 && (
                      <span className="bg-purple-600/30 border border-purple-500/40 text-purple-300 text-[10px] font-black px-2 py-0.5 rounded-full">
                        {activeCount} ativo{activeCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Refinando pesquisa em <span className="text-slate-200 font-semibold">{gameName}</span>
                  </p>
                </div>
              </div>

              <button
                id="advanced-filters-close-btn"
                onClick={onClose}
                className="w-8 h-8 rounded-xl bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
                title="Fechar (Esc)"
              >
                <i className="fas fa-xmark text-sm"></i>
              </button>
            </div>

            {/* Conteúdo com campos de formulário */}
            <div className="p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar">
              {advancedFields.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-xs">
                  Não há campos avançados disponíveis para este jogo.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {advancedFields.map((field) => {
                    const rawAll = activeFilters[`${field}__all`];
                    const rawOr = activeFilters[field];
                    const isAll = Boolean(rawAll !== undefined && rawAll !== null && rawAll.trim() !== '');
                    const value = isAll ? (rawAll || '') : (rawOr || '');
                    const hasValue = Boolean(value.trim());
                    const hasMultiple = value.includes(',');

                    return (
                      <div key={field} className="space-y-1.5">
                        <div className="flex items-center justify-between text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                          <label htmlFor={`filter-input-${field}`} className="truncate pr-1">
                            {pretty(field)}
                          </label>
                          <div className="flex items-center space-x-1.5 shrink-0">
                            {hasMultiple && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (isAll) {
                                    onFilterChange(field, value);
                                  } else {
                                    onFilterChange(`${field}__all`, value);
                                  }
                                }}
                                className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider transition-all flex items-center space-x-1 border cursor-pointer active:scale-95 ${
                                  isAll
                                    ? 'bg-purple-600 text-white border-purple-400 shadow-sm shadow-purple-600/30'
                                    : 'bg-slate-800 text-slate-300 border-slate-700 hover:text-white'
                                }`}
                                title={isAll ? "Modo AND (__all) ativo: exige todos os valores. Clique para alternar para OU." : "Modo OR ativo: qualquer valor pode corresponder. Clique para alternar para E (__all)."}
                              >
                                <i className={`fas ${isAll ? 'fa-link' : 'fa-code-fork'} text-[7px]`}></i>
                                <span>{isAll ? 'E (AND)' : 'OU (OR)'}</span>
                              </button>
                            )}
                            {hasValue && (
                              <button
                                type="button"
                                onClick={() => {
                                  onFilterChange(field, '');
                                  onFilterChange(`${field}__all`, '');
                                }}
                                className="text-[10px] text-pink-400 hover:text-pink-300 font-semibold lowercase cursor-pointer"
                                title="Limpar campo"
                              >
                                limpar
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="relative">
                          <input
                            id={`filter-input-${field}`}
                            type="text"
                            placeholder={`Ex: valor1, valor2...`}
                            value={value}
                            onChange={(e) => {
                              const targetKey = isAll ? `${field}__all` : field;
                              onFilterChange(targetKey, e.target.value);
                            }}
                            className={`w-full bg-slate-950 border rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-slate-600 outline-none transition-all ${
                              hasValue 
                                ? 'border-purple-500/80 bg-purple-950/10 shadow-sm shadow-purple-900/20' 
                                : 'border-slate-800 hover:border-slate-700 focus:border-purple-500'
                            }`}
                          />
                          {hasValue && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Rodapé com Ações */}
            <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/90 flex items-center justify-between gap-3">
              {activeCount > 0 ? (
                <button
                  id="advanced-filters-clear-all-btn"
                  type="button"
                  onClick={onClearFilters}
                  className="px-4 py-2.5 rounded-xl bg-pink-600/10 hover:bg-pink-600/20 border border-pink-500/20 text-pink-400 hover:text-pink-300 text-xs font-bold transition-colors flex items-center space-x-2"
                >
                  <i className="fas fa-trash-can text-[11px]"></i>
                  <span>Limpar Filtros</span>
                </button>
              ) : (
                <div className="text-xs text-slate-500 italic">
                  Preencha os campos para filtrar.
                </div>
              )}

              <button
                id="advanced-filters-apply-btn"
                type="button"
                onClick={onClose}
                className="ml-auto px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold shadow-lg shadow-purple-600/30 transition-all flex items-center space-x-2"
              >
                <span>Aplicar e Fechar</span>
                <i className="fas fa-check text-[11px]"></i>
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
